import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, Clock, Calendar } from "lucide-react";
import { toast } from "sonner";
import { useReservationCategories } from "@/hooks/reservations/useReservationCategories";
import { useReservationItems } from "@/hooks/reservations/useReservationItems";
import { useReservationTimeSlots } from "@/hooks/reservations/useReservationTimeSlots";
import { useManageTimeSlots } from "@/hooks/reservations/useManageTimeSlots";
import { DatePicker } from "@/components/ui/date-picker";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { format } from "date-fns";
import { es } from "date-fns/locale";

// Helper function to parse date strings locally without timezone issues
const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

interface TimeSlotManagerProps {
  gymId: string;
}

const DAYS_OF_WEEK = [
  { value: 0, label: "Domingo" },
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
];

export const TimeSlotManager = ({ gymId }: TimeSlotManagerProps) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [isCreating, setIsCreating] = useState(false);
  const [slotType, setSlotType] = useState<"recurring" | "specific">("recurring");
  const [newTimeSlot, setNewTimeSlot] = useState({
    day_of_week: "",
    specific_date: "",
    start_time: "",
    end_time: "",
  });

  const { data: categories = [] } = useReservationCategories(gymId);
  const { data: items = [] } = useReservationItems(selectedCategoryId);
  const { data: timeSlots = { recurring: [], specific: [], all: [] } } = useReservationTimeSlots(selectedItemId);
  const { createTimeSlot, deleteTimeSlot } = useManageTimeSlots();

  const handleCreate = async () => {
    if (!selectedItemId || !newTimeSlot.start_time || !newTimeSlot.end_time) {
      toast.error("Todos los campos son obligatorios");
      return;
    }

    if (slotType === "recurring" && !newTimeSlot.day_of_week) {
      toast.error("Selecciona un día de la semana");
      return;
    }

    if (slotType === "specific" && !newTimeSlot.specific_date) {
      toast.error("Selecciona una fecha específica");
      return;
    }

    if (newTimeSlot.start_time >= newTimeSlot.end_time) {
      toast.error("La hora de inicio debe ser anterior a la hora de fin");
      return;
    }

    const timeSlotData: any = {
      item_id: selectedItemId,
      start_time: newTimeSlot.start_time,
      end_time: newTimeSlot.end_time,
    };

    if (slotType === "recurring") {
      timeSlotData.day_of_week = parseInt(newTimeSlot.day_of_week);
    } else {
      timeSlotData.specific_date = newTimeSlot.specific_date;
    }

    createTimeSlot.mutate(timeSlotData, {
      onSuccess: () => {
        setIsCreating(false);
        setNewTimeSlot({
          day_of_week: "",
          specific_date: "",
          start_time: "",
          end_time: "",
        });
      },
    });
  };

  const handleCancel = () => {
    setIsCreating(false);
    setNewTimeSlot({
      day_of_week: "",
      specific_date: "",
      start_time: "",
      end_time: "",
    });
  };

  const handleDeleteTimeSlot = (timeSlotId: string) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar este horario?")) {
      deleteTimeSlot.mutate(timeSlotId);
    }
  };

  const formatTimeSlot = (startTime: string, endTime: string) => {
    return `${startTime.slice(0, 5)} - ${endTime.slice(0, 5)}`;
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="category">Categoría</Label>
          <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
            <SelectTrigger>
              <SelectValue placeholder="Selecciona una categoría" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="item">Elemento</Label>
          <Select 
            value={selectedItemId} 
            onValueChange={setSelectedItemId}
            disabled={!selectedCategoryId}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecciona un elemento" />
            </SelectTrigger>
            <SelectContent>
              {items.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedItemId && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Horarios Configurados</CardTitle>
            <Button
              onClick={() => setIsCreating(true)}
              disabled={isCreating}
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Crear Horario
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {isCreating && (
              <Card className="border-dashed">
                <CardHeader>
                  <CardTitle className="text-lg">Nuevo Horario</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Tipo de Horario</Label>
                    <RadioGroup
                      value={slotType}
                      onValueChange={(value: "recurring" | "specific") => setSlotType(value)}
                      className="flex gap-6 mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="recurring" id="recurring" />
                        <Label htmlFor="recurring" className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Horario Recurrente
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="specific" id="specific" />
                        <Label htmlFor="specific" className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          Fecha Específica
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {slotType === "recurring" ? (
                    <div>
                      <Label htmlFor="day_of_week">Día de la Semana</Label>
                      <Select
                        value={newTimeSlot.day_of_week}
                        onValueChange={(value) =>
                          setNewTimeSlot({ ...newTimeSlot, day_of_week: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona un día" />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_WEEK.map((day) => (
                            <SelectItem key={day.value} value={day.value.toString()}>
                              {day.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="specific_date">Fecha Específica</Label>
                      <DatePicker
                        value={newTimeSlot.specific_date}
                        onChange={(value) =>
                          setNewTimeSlot({ ...newTimeSlot, specific_date: value })
                        }
                        placeholder="Selecciona una fecha"
                        className="w-full"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start_time">Hora de Inicio</Label>
                      <Input
                        id="start_time"
                        type="time"
                        value={newTimeSlot.start_time}
                        onChange={(e) =>
                          setNewTimeSlot({ ...newTimeSlot, start_time: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_time">Hora de Fin</Label>
                      <Input
                        id="end_time"
                        type="time"
                        value={newTimeSlot.end_time}
                        onChange={(e) =>
                          setNewTimeSlot({ ...newTimeSlot, end_time: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      onClick={handleCreate}
                      disabled={createTimeSlot.isPending}
                    >
                      {createTimeSlot.isPending ? "Creando..." : "Crear"}
                    </Button>
                    <Button variant="outline" onClick={handleCancel}>
                      Cancelar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Horarios Recurrentes */}
            {timeSlots.recurring && timeSlots.recurring.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Horarios Recurrentes
                </h4>
                <div className="space-y-3">
                  {DAYS_OF_WEEK.map((day) => {
                    const daySlots = timeSlots.recurring.filter(
                      (slot: any) => slot.day_of_week === day.value
                    );
                    if (daySlots.length === 0) return null;

                    return (
                      <div key={day.value} className="border rounded-lg p-3">
                        <h5 className="font-medium mb-2">{day.label}</h5>
                        <div className="flex flex-wrap gap-2">
                          {daySlots.map((slot: any) => (
                            <div
                              key={slot.id}
                              className="flex items-center gap-2 bg-muted px-3 py-1 rounded-md text-sm"
                            >
                              <span>{formatTimeSlot(slot.start_time, slot.end_time)}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteTimeSlot(slot.id)}
                                disabled={deleteTimeSlot.isPending}
                                className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Fechas Específicas */}
            {timeSlots.specific && timeSlots.specific.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Fechas Específicas
                </h4>
                <div className="space-y-3">
                  {timeSlots.specific
                    .sort((a: any, b: any) => parseLocalDate(a.specific_date).getTime() - parseLocalDate(b.specific_date).getTime())
                    .map((slot: any) => (
                      <div key={slot.id} className="border rounded-lg p-3">
                        <h5 className="font-medium mb-2">
                          {format(parseLocalDate(slot.specific_date), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}
                        </h5>
                        <div className="flex items-center gap-2 bg-muted px-3 py-1 rounded-md text-sm w-fit">
                          <span>{formatTimeSlot(slot.start_time, slot.end_time)}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteTimeSlot(slot.id)}
                            disabled={deleteTimeSlot.isPending}
                            className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {timeSlots.recurring?.length === 0 && timeSlots.specific?.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Sin horarios configurados</p>
                <p className="text-sm">Crea el primer horario para este elemento</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!selectedCategoryId && (
        <div className="text-center py-8 text-muted-foreground">
          <p>Selecciona una categoría para comenzar</p>
        </div>
      )}

      {selectedCategoryId && !selectedItemId && (
        <div className="text-center py-8 text-muted-foreground">
          <p>Selecciona un elemento para gestionar sus horarios</p>
        </div>
      )}
    </div>
  );
};